import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { emitter } from 'shared/helpers/mitt';

import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { useI18n } from 'vue-i18n';
import CurrentAgentPauseAPI from 'dashboard/api/currentAgentPause';

const defaultPauseState = () => ({
  active_pause: null,
  pending_pause: null,
  legacy_pause: null,
  templates: [],
});

const normalizePauseState = data => ({
  ...defaultPauseState(),
  ...(data || {}),
});

export function useCurrentAgentPause({ pollIntervalMs = 15000 } = {}) {
  const { t } = useI18n();

  const pauseState = ref(defaultPauseState());
  const isPauseStateLoading = ref(false);
  const isPauseActionLoading = ref(false);

  let pollingIntervalId = null;

  const activePause = computed(() => pauseState.value.active_pause);
  const pendingPause = computed(() => pauseState.value.pending_pause);
  const legacyPause = computed(() => pauseState.value.legacy_pause);
  const blockingPause = computed(() => activePause.value || legacyPause.value);
  const hasBlockingPause = computed(() => Boolean(blockingPause.value));

  const updatePauseState = data => {
    pauseState.value = normalizePauseState(data);
  };

  const fetchPauseState = async ({ silent = false } = {}) => {
    if (!silent) {
      isPauseStateLoading.value = true;
    }

    try {
      const response = await CurrentAgentPauseAPI.get();
      updatePauseState(response.data);
    } catch (error) {
      if (!silent) {
        useAlert(t('AGENT_MGMT.PAUSE_SELF.API.FETCH_ERROR'));
      }
    } finally {
      if (!silent) {
        isPauseStateLoading.value = false;
      }
    }
  };

  const broadcastPauseUpdate = data => {
    emitter.emit('agent_pause_updated', data);
  };

  const requestPause = async pauseTemplateId => {
    isPauseActionLoading.value = true;

    try {
      const response = await CurrentAgentPauseAPI.create(pauseTemplateId);
      updatePauseState(response.data);
      broadcastPauseUpdate(response.data);

      if (response.data.pending_pause) {
        useAlert(t('AGENT_MGMT.PAUSE_SELF.API.REQUESTED_SUCCESS'));
      } else {
        useAlert(t('AGENT_MGMT.PAUSE_SELF.API.STARTED_SUCCESS'));
      }
    } catch (error) {
      useAlert(
        error?.response?.data?.error ||
          t('AGENT_MGMT.PAUSE_SELF.API.ACTION_ERROR')
      );
    } finally {
      isPauseActionLoading.value = false;
    }
  };

  const stopPause = async () => {
    isPauseActionLoading.value = true;

    try {
      const response = await CurrentAgentPauseAPI.delete();
      updatePauseState(response.data);
      broadcastPauseUpdate(response.data);
      useAlert(t('AGENT_MGMT.PAUSE_SELF.API.STOP_SUCCESS'));
    } catch (error) {
      useAlert(
        error?.response?.data?.error ||
          t('AGENT_MGMT.PAUSE_SELF.API.ACTION_ERROR')
      );
    } finally {
      isPauseActionLoading.value = false;
    }
  };

  const startPolling = () => {
    if (pollIntervalMs <= 0 || pollingIntervalId) return;

    pollingIntervalId = window.setInterval(() => {
      fetchPauseState({ silent: true });
    }, pollIntervalMs);
  };

  const stopPolling = () => {
    if (!pollingIntervalId) return;

    window.clearInterval(pollingIntervalId);
    pollingIntervalId = null;
  };

  useEmitter('agent_pause_updated', () => fetchPauseState({ silent: true }));

  onMounted(() => {
    fetchPauseState();
    startPolling();
  });

  onBeforeUnmount(() => {
    stopPolling();
  });

  return {
    pauseState,
    activePause,
    pendingPause,
    legacyPause,
    blockingPause,
    hasBlockingPause,
    isPauseStateLoading,
    isPauseActionLoading,
    fetchPauseState,
    requestPause,
    stopPause,
  };
}
